// ============================================================
// The asset library's records - what a GM keeps across campaigns.
//
// One library per hosting account, under `owners/{uid}/…` on both backends
// (plan dev-docs/plans/assets-library.md). Folders are rows with a parent;
// an asset is one small row the page watches, and its content lives in
// versions read on demand. Pure types, zero React.
// ============================================================

/**
 * The kinds stored as text, in versioned documents: stage 1's maps and palettes, stage 2's scenes and characters,
 * the Armoury's weapons, effects, area templates, loadouts and modifiers (plan armoury, `armouryKinds.ts`), and
 * Tactics - squad abilities and their Oaths (Squad Mode, `engine/killTeam/tactics.ts`) - and the bundles a sheet
 * carries, Always on items and Solo modes (plan modes-rework, `engine/character/soloModes.ts`).
 */
const DOC_KINDS = [
  'map',
  'palette',
  'scene',
  'character',
  'weapon',
  'effect',
  'area',
  'loadout',
  'modifier',
  'module',
  'tactics',
  'alwaysOn',
  'soloMode',
] as const;
export type DocKind = (typeof DOC_KINDS)[number];

/** The kinds stored as files through the files port (stage 3): a picture, and a document to hand the table (a PDF). */
export const FILE_KINDS = ['image', 'handout'] as const;
export type FileKind = (typeof FILE_KINDS)[number];

/** What an asset can be. */
export const ASSET_KINDS = [...DOC_KINDS, ...FILE_KINDS] as const;
export type AssetKind = DocKind | FileKind;

/** Is this kind kept as a file, not as text? */
export const isFileKind = (kind: AssetKind): kind is FileKind => (FILE_KINDS as readonly string[]).includes(kind);

/** The folder every asset and folder sits in when it names none: "My assets". */
export const ROOT = '';

export interface FolderRow {
  id: string;
  name: string;
  /** `ROOT` for a top-level folder. */
  parentId: string;
  createdAt: number;
  updatedAt: number;
}

/** What a Teams-view change did to a version (plan teams-manager): rename and move read "from -> to". */
export type TeamChangeKind =
  | 'rename'
  | 'move'
  | 'colour'
  | 'ally'
  | 'merge'
  | 'delete'
  | 'unify'
  | 'split'
  | 'undo'
  // The kill-team block set or switched off (`to`: the Oath now played), and the leader moved (`to`: his name).
  | 'killTeam'
  | 'leader';

/**
 * What made a version - data, not a sentence, so a renamed session or a new
 * language does not leave stale words behind (`noteText` says it).
 */

export interface Note {
  kind: 'library' | 'table' | 'restored' | 'imported' | 'duplicated' | 'team';
  /** The session a table save came from. */
  sessionId?: string;
  sessionName?: string;
  /** The version restored, the file imported, the asset duplicated; for a team change, the team it was. */
  from?: string | number;
  /** A team change: what it did, and the team it went to (or stood beside); none for "no team". */
  change?: TeamChangeKind;
  to?: string;
}

export interface AssetRow {
  id: string;
  kind: AssetKind;
  name: string;
  /** `ROOT` for My assets. */
  folderId: string;
  tags: string[];
  /** The number the next save reserves. Ahead of `version` while a save is in flight, or after one that stopped. */
  nextVersion: number;
  /** The newest version saved WHOLE; 0 before the first. */
  version: number;
  /** Characters in the newest version. */
  size: number;
  /** Characters in every version kept - what the meter counts. */
  bytes: number;
  /** The version the card's picture shows; 0 for none yet. */
  thumbVersion: number;
  lastNote: Note | null;
  createdAt: number;
  updatedAt: number;
  /** When it went to the Trash; 0 while it is live. */
  deletedAt: number;
  /** The folder it was in when trashed, so a restore puts it back. */
  trashedFrom: string;
}

/** One saved version's facts, beside its content. */
export interface VersionMeta {
  version: number;
  /** How many parts the content is in. */
  parts: number;
  size: number;
  note: Note;
  at: number;
  /** The account that saved it. */
  by: string;
}

/** Where a session's copy came from: the asset and the version copied. Carried by the copy, never by the terrain. */
export interface Origin {
  assetId: string;
  version: number;
}
